/**
 * twgSchemaService - Fetches node and relationship schema data from the
 * TWG (Teamwork Graph) schema API endpoints.
 *
 * These endpoints return more targeted data than the legacy registered-types.json
 * endpoints, with fewer invalid/irrelevant relationships that need filtering.
 *
 * The responses are arrays which are converted to Record<string, T> format
 * for compatibility with existing consumers (cypherQueryService, explorationActions).
 */

import axios from 'axios';
import { atom, type Atom } from 'jotai';
import { loadable } from 'jotai/utils';

import { request } from '@atlassian/twg-common/request';

import { getConfig } from '../../../../../../config';
import type { Node } from '../../../../labs/home/hooks/useNodes';
import type { Relationship } from '../../../../labs/home/hooks/useRelationships';
import type { DataChangeStreams } from '../RelationshipTypes';
import type { AtiNodeMetadata } from '../utils/ariUtils';
import { parseMetadataFields } from './metadataFieldParser';

// ============================================================================
// TWG endpoint configuration
// ============================================================================

/**
 * Resolves the base URL for TWG schema API.
 *
 * In local dev, requests go through the proxy (/gateway → twgGatewayUrl/gateway).
 * In deployed environments, the full URL is used.
 */
function getTwgSchemaBaseUrl(): string {
	const isLocalDev =
		typeof window !== 'undefined' &&
		(window.location.hostname === 'localhost' || window.location.hostname.includes('localhost'));

	if (isLocalDev) {
		return '/gateway/api/twg-api/v2/twg-schema';
	}

	if (getConfig().env === 'staging') {
		return 'https://teamwork-graph.stg.atlassian.com/gateway/api/twg-api/v2/twg-schema';
	}

	// In deployed environments, use the production TWG gateway
	return 'https://teamwork-graph.atlassian.com/gateway/api/twg-api/v2/twg-schema';
}

// ============================================================================
// Raw TWG response types
// ============================================================================

/** Shape of a single node entry from the TWG schema nodes endpoint */
export interface TwgSchemaNode {
	id: string;
	name: string;
	types: string[];
	displayName: string;
	category: string;
	providerType: string;
	deleted: boolean;
	isNodeExternallyAvailable: boolean;
	aliases?: Array<{
		name: string;
		displayName: string;
		isLatest: boolean;
		deprecated: boolean;
		isAliasExternallyAvailable: boolean;
		isHiddenFromGraphQLSchema: boolean;
	}>;
	ownerInfo?: {
		serviceId?: string;
		pointOfContact?: string;
		team?: string;
		opsgenieTeamId?: string;
	};
	graphQL?: {
		hydration?: {
			type?: string[];
		};
	};
	metadataDefinition?: string;
	mandatoryMetadataFieldsOptOut?: Record<string, boolean>;
	version?: string;
	/**
	 * Stored-metadata definitions. The jq programs under
	 * `updateRecords[].metadata` describe the fields physically stored in UGS /
	 * indexed in OpenSearch — i.e. the fields queryable in a Cypher `WHERE`.
	 * The endpoint returns this today; older code discarded it during
	 * conversion.
	 */
	dataChangeStreams?: Node['dataChangeStreams'];
}

/** Shape of a single relationship entry from the TWG schema relationships endpoint */
export interface TwgSchemaRelationship {
	name: string;
	id?: string;
	fromType: string[];
	targetType: string[];
	fromTypeName: string[];
	targetTypeName: string[];
	category: string;
	hidden?: boolean;
	description?: string;
	isRelationshipExternallyAvailable?: boolean;
	ownerInfo?: {
		serviceId?: string;
		pointOfContact?: string;
		team?: string;
		opsgenieTeamId?: string;
	};
	permissionCheck?: Record<string, any>;
	sendsAnalytics?: string;
	sendsToFlock?: boolean;
	providerType?: string;
	version?: string;
	/**
	 * Stored-metadata definitions. The jq programs under the
	 * `createEvents[]` / `updateEvents[]` / `objectUpdateRecord[]` entries
	 * (`openSearchObjectMetadata`) describe the OpenSearch-indexed fields a
	 * Cypher `WHERE` can filter on.
	 */
	dataChangeStreams?: DataChangeStreams;
}

// ============================================================================
// Conversion helpers
// ============================================================================

/**
 * Converts a TWG node entry to the existing Node type used by the codebase.
 * Fields not present in the TWG response are filled with sensible defaults.
 *
 * The `graphQL.hydration.type` is populated from the TWG node's
 * `graphQL.hydration.type` field when available, which contains the actual
 * GraphQL type names used by the AGG gateway for hydrating Cypher query
 * results. These can differ from the node's `name` — e.g., an
 * `IdentityUser` node hydrates to `["AtlassianAccountUser", "CustomerUser",
 * "AppUser"]`. Falls back to `[twgNode.name]` when the API response does
 * not include explicit hydration types.
 */
function convertTwgNode(twgNode: TwgSchemaNode): Node {
	// Use the explicit hydration types from the API when available;
	// fall back to the node name if the field is missing or empty.
	const hydrationTypes =
		twgNode.graphQL?.hydration?.type && twgNode.graphQL.hydration.type.length > 0
			? twgNode.graphQL.hydration.type
			: [twgNode.name];

	return {
		id: twgNode.id,
		name: twgNode.name,
		types: twgNode.types,
		displayName: twgNode.displayName,
		category: twgNode.category,
		providerType: twgNode.providerType,
		deleted: twgNode.deleted,
		isNodeExternallyAvailable: twgNode.isNodeExternallyAvailable,
		persistedIn: [],
		aliases: twgNode.aliases,
		ownerInfo: {
			serviceId: twgNode.ownerInfo?.serviceId ?? '',
			pointOfContact: twgNode.ownerInfo?.pointOfContact ?? '',
			team: twgNode.ownerInfo?.team ?? '',
			opsgenieTeamId: twgNode.ownerInfo?.opsgenieTeamId ?? '',
		},
		graphQL: { hydration: { type: hydrationTypes } },
		metadataDefinition: twgNode.metadataDefinition ?? '',
		mandatoryMetadataFieldsOptOut: {
			createdAt: twgNode.mandatoryMetadataFieldsOptOut?.createdAt ?? false,
			lastUpdated: twgNode.mandatoryMetadataFieldsOptOut?.lastUpdated ?? false,
			createdBy: twgNode.mandatoryMetadataFieldsOptOut?.createdBy ?? false,
			updatedBy: twgNode.mandatoryMetadataFieldsOptOut?.updatedBy ?? false,
		},
		dataChangeStreams: twgNode.dataChangeStreams ?? { updateRecords: [], deleteRecords: [] },
		version: twgNode.version ?? '',
	};
}

/**
 * Converts a TWG relationship entry to the existing Relationship type.
 * Fields not present in the TWG response are filled with sensible defaults.
 */
function convertTwgRelationship(twgRel: TwgSchemaRelationship): Relationship {
	return {
		name: twgRel.name,
		id: twgRel.id ?? twgRel.name,
		fromType: twgRel.fromType,
		targetType: twgRel.targetType,
		fromTypeName: twgRel.fromTypeName,
		targetTypeName: twgRel.targetTypeName,
		category: twgRel.category,
		hidden: twgRel.hidden ?? false,
		deleted: false, // TWG endpoints don't include deleted relationships
		availableIn: ['production'], // TWG endpoints serve production data
		sendsAnalytics: twgRel.sendsAnalytics ?? '',
		sendsToFlock: twgRel.sendsToFlock ?? false,
		ownerInfo: {
			pointOfContact: twgRel.ownerInfo?.pointOfContact ?? '',
			team: twgRel.ownerInfo?.team ?? '',
			opsgenieTeamId: twgRel.ownerInfo?.opsgenieTeamId ?? '',
		},
		permissionCheck: twgRel.permissionCheck ?? {},
		version: twgRel.version ?? '',
		providerType: {
			source: [],
			target: [],
		},
		// Retain the stored-metadata definitions so consumers (e.g. the
		// queryable-metadata panel) can surface the Cypher-filterable fields.
		dataChangeStreams: twgRel.dataChangeStreams,
	};
}

// ============================================================================
// Jotai atoms
// ============================================================================

export type NodeAsyncAtomValue =
	| {
			success: true;
			data: Record<string, Node>;
			/**
			 * Key: alias
			 * Value: original node name
			 */
			aliasMapToOriginal: Record<string, string>;
	  }
	| {
			success: false;
			error: any;
	  };

/**
 * These are nodes which are going to be ignored when creating the
 * `aliasMapToOriginal`. They are test nodes which are overriding the actual
 * node names.
 */
const IGNORE_NODE_NAMES: string[] = ['TestJiraIssue'] as const;

/**
 * Fetches nodes from the TWG schema API and returns them as Record<string, Node>
 * keyed by node name, for compatibility with existing consumers.
 */
export const twgNodesAsyncAtom: Atom<Promise<NodeAsyncAtomValue>> = atom(
	async (): Promise<NodeAsyncAtomValue> => {
		const baseUrl = getTwgSchemaBaseUrl();
		// TODO: need to do error handling for errors
		const res = await request<TwgSchemaNode[]>(
			'GET',
			`${baseUrl}/nodes`,
			undefined,
			{
				'Content-Type': 'application/json',
				credentials: 'include',
			},
			[200],
		);

		if (res.success) {
			const nodesRecords: Record<string, Node> = res.data
				.filter((node) => !node.deleted)
				.map((node) => convertTwgNode(node))
				.reduce((acc: Record<string, Node>, node) => {
					acc[node.name] = node;
					return acc;
				}, {});

			const aliasMapToOriginal: Record<string, string> = res.data
				.filter((node) => !IGNORE_NODE_NAMES.includes(node.name))
				.flatMap((node) => {
					return [
						...(node.graphQL?.hydration?.type?.map((alias) => [alias, node.name]) ?? []),
						...(node.aliases?.map((alias) => [alias.name, node.name]) ?? []),
					];
				})
				.reduce((acc: Record<string, string>, [aliasName, originalName]) => {
					// Keep the first owner when malformed or test schema data repeats
					// an alias; otherwise a later node can hide the real canonical node.
					if (!(aliasName in acc)) {
						acc[aliasName] = originalName;
					}
					return acc;
				}, {});

			return {
				success: true,
				data: nodesRecords,
				aliasMapToOriginal,
			};
		} else {
			console.log('error with twgNodesAsyncAtom fetching', res.error, res.statusCode);
			return {
				success: false,
				error: res.error,
			};
		}
	},
);

/**
 * Fetches relationships from the TWG schema API and returns them as
 * Record<string, Relationship> keyed by relationship name, for compatibility
 * with existing consumers. The converted entries retain `dataChangeStreams`, so
 * `RelationshipQueryableFieldsMapAtom` can derive the Cypher-queryable fields
 * without a second request.
 */
export const twgRelationshipsAsyncAtom: Atom<Promise<Record<string, Relationship>>> = atom(
	async () => {
		const baseUrl = getTwgSchemaBaseUrl();
		const response = await axios.get<TwgSchemaRelationship[]>(`${baseUrl}/relationships`, {
			headers: { 'Content-Type': 'application/json' },
			withCredentials: true,
		});

		const relsRecord: Record<string, Relationship> = {};
		for (const twgRel of response.data) {
			// Filter out:
			// - hidden relationships (internal/deprecated)
			// - relationships with sendsToFlock=false (not available in AGS/Cypher queries,
			//   which causes index mismatches in UNION ALL discovery queries)
			if (!twgRel.hidden && twgRel.sendsToFlock !== false) {
				const converted = convertTwgRelationship(twgRel);
				relsRecord[twgRel.name] = converted;
			}
		}
		return relsRecord;
	},
);

/**
 * Derived atom that maps GraphQL hydration type names to human-readable
 * TWG display names (e.g. "AtlassianAccountUser" → "Identity User").
 *
 * Uses `loadable` to safely read the async nodes atom without suspending.
 * Returns a stable empty record while loading or on error so downstream
 * useMemo hooks don't see a new object reference every render.
 */
const loadableTwgNodes = loadable(twgNodesAsyncAtom);

/**
 * Unified atom that maps ATI type strings to node metadata (typename and
 * display name).
 *
 * This is the single source of truth for resolving node type information.
 * Every node's ARI can be parsed to extract the ATI, which is then looked
 * up here to get both the canonical typename (for grouping/lookups) and
 * the human-readable display name.
 *
 * e.g. "ati:cloud:confluence:page" → { typename: "ConfluencePage", displayName: "Confluence Page" }
 */
const EMPTY_ATI_NODE_MAP: Record<string, AtiNodeMetadata> = {};

/**
 * Derived atom that maps canonical TWG node names (PascalCase) to
 * human-readable display names.
 *
 * Used by relationship display utilities where the relationship schema
 * provides PascalCase node names (`fromTypeName` / `targetTypeName`)
 * that need to be resolved to display names.
 *
 * e.g. "ThirdPartyUser" → "Third Party User"
 *      "ConfluencePage" → "Confluence Page"
 */
const EMPTY_NODE_NAME_MAP: Record<string, string> = {};

export const NodeNameDisplayMapAtom: Atom<Record<string, string>> = atom<Record<string, string>>(
	(get) => {
		const loaded = get(loadableTwgNodes);
		if (loaded.state !== 'hasData') {
			return EMPTY_NODE_NAME_MAP;
		}
		const result = loaded.data;
		if (!result.success) {
			return EMPTY_NODE_NAME_MAP;
		}
		const map: Record<string, string> = {};
		for (const node of Object.values(result.data)) {
			map[node.name] = node.displayName;
		}
		return map;
	},
);

export const AtiNodeMetadataMapAtom: Atom<Record<string, AtiNodeMetadata>> = atom<
	Record<string, AtiNodeMetadata>
>((get) => {
	const loaded = get(loadableTwgNodes);
	if (loaded.state !== 'hasData') {
		return EMPTY_ATI_NODE_MAP;
	}
	const result = loaded.data;
	if (!result.success) {
		return EMPTY_ATI_NODE_MAP;
	}
	const map: Record<string, AtiNodeMetadata> = {};
	for (const node of Object.values(result.data)) {
		const metadata: AtiNodeMetadata = {
			typename: node.name,
			displayName: node.displayName,
		};
		for (const atiType of node.types) {
			map[atiType] = metadata;
		}
	}
	return map;
});

const EMPTY_NODE_TYPE_TO_ATI_MAP: Record<string, string[]> = {};

/**
 * Maps node typename → ATI type strings.
 * e.g. { "ConfluencePage": ["ati:cloud:confluence:page"], "JiraIssue": ["ati:cloud:jira:work-item", ...] }
 *
 * Used by content-entity-links-entity grouping to derive the product
 * from discovered node types via the same ATI-based resolution as
 * regular relationships.
 */
export const NodeTypeToAtiTypesMapAtom: Atom<Record<string, string[]>> = atom<
	Record<string, string[]>
>((get) => {
	const loaded = get(loadableTwgNodes);
	if (loaded.state !== 'hasData') {
		return EMPTY_NODE_TYPE_TO_ATI_MAP;
	}
	const result = loaded.data;
	if (!result.success) {
		return EMPTY_NODE_TYPE_TO_ATI_MAP;
	}
	const map: Record<string, string[]> = {};
	for (const node of Object.values(result.data)) {
		map[node.name] = node.types;
	}
	return map;
});

// ============================================================================
// Stored (Cypher-queryable) metadata field maps
// ============================================================================

/**
 * Stable empty record so downstream consumers reading a queryable-fields map
 * while the schema is loading (or on error) don't see a new object reference on
 * every render.
 */
const EMPTY_QUERYABLE_FIELDS_MAP: Record<string, string[]> = {};

/**
 * Maps a node type to the list of stored, Cypher-queryable field names.
 *
 * The fields are parsed from the node's `dataChangeStreams.updateRecords[].metadata`
 * jq definitions (retained during conversion). Each entry is keyed by BOTH the
 * canonical TWG node name (e.g. "IdentityUser") and every GraphQL hydration
 * alias (e.g. "AtlassianAccountUser") so the details panel can look the fields
 * up whether it holds the node name or a hydration alias in `typename`.
 *
 * Schema-declared intrinsic metadata fields (for example `createdAt`) are
 * included unless the node explicitly opts out. Node types with neither
 * intrinsic nor custom stored metadata are omitted.
 */
export const NodeQueryableFieldsMapAtom: Atom<Record<string, string[]>> = atom<
	Record<string, string[]>
>((get) => {
	const loaded = get(loadableTwgNodes);
	if (loaded.state !== 'hasData') {
		return EMPTY_QUERYABLE_FIELDS_MAP;
	}
	const result = loaded.data;
	if (!result.success) {
		return EMPTY_QUERYABLE_FIELDS_MAP;
	}

	const map: Record<string, string[]> = {};
	for (const node of Object.values(result.data)) {
		const customFields = parseMetadataFields(
			(node.dataChangeStreams?.updateRecords ?? []).map((record) => record.metadata),
		);
		const intrinsicFields = Object.entries(node.mandatoryMetadataFieldsOptOut ?? {})
			.filter(([, optedOut]) => !optedOut)
			.map(([field]) => field);
		const fields = [...new Set([...intrinsicFields, ...customFields])].sort();
		if (fields.length === 0) {
			continue;
		}
		map[node.name] = fields;
		for (const alias of node.graphQL?.hydration?.type ?? []) {
			map[alias] = fields;
		}
	}
	return map;
});

/**
 * Loadable wrapper over the relationships fetch so the queryable-fields map can
 * be derived synchronously without suspending.
 */
const loadableTwgRelationships = loadable(twgRelationshipsAsyncAtom);

/**
 * Maps a relationship type to the list of stored, Cypher-queryable field names.
 *
 * The fields are parsed from the `openSearchObjectMetadata` jq definitions on the
 * relationship's `createEvents[]` / `updateEvents[]` / `objectUpdateRecord[]`
 * entries — the OpenSearch-indexed (and therefore Cypher-filterable) set. Each
 * entry is keyed by BOTH the schema relationship name (kebab-case, e.g.
 * "atlassian-user-assigned-jira-work-item") and its underscore form
 * (e.g. "atlassian_user_assigned_jira_work_item") so the details panel can look
 * the fields up regardless of which form the edge's `relationshipType` uses.
 *
 * Hidden / non-flock relationships are already excluded by
 * `twgRelationshipsAsyncAtom`; relationship types with no stored metadata are
 * omitted here.
 */
export const RelationshipQueryableFieldsMapAtom: Atom<Record<string, string[]>> = atom<
	Record<string, string[]>
>((get) => {
	const loaded = get(loadableTwgRelationships);
	if (loaded.state !== 'hasData') {
		return EMPTY_QUERYABLE_FIELDS_MAP;
	}

	const map: Record<string, string[]> = {};
	for (const rel of Object.values(loaded.data)) {
		const dataChangeStreams = rel.dataChangeStreams;
		if (!dataChangeStreams) {
			continue;
		}

		const jqStrings: Array<string | undefined> = [
			...(dataChangeStreams.createEvents ?? []).map((event) => event.openSearchObjectMetadata),
			...(dataChangeStreams.updateEvents ?? []).map((event) => event.openSearchObjectMetadata),
			...(dataChangeStreams.objectUpdateRecord ?? []).map(
				(record) => record.openSearchObjectMetadata,
			),
		];

		const fields = parseMetadataFields(jqStrings);
		if (fields.length === 0) {
			continue;
		}
		map[rel.name] = fields;
		const underscored = rel.name.replace(/-/g, '_');
		if (underscored !== rel.name) {
			map[underscored] = fields;
		}
	}
	return map;
});
